"use client"

import { getPageNameFromPath } from "@/app/(site)/_utils/getPageNameFromPath"
import { type ProductData } from "@/lib/content-service/types/ContentServiceProduct"
import { adaptProductForTracking } from "@/lib/utils/tracking/itemDataTransformer"
import { useSiteContext } from "@/lib/utils/tracking/siteContext"
import { usePathname } from "next/navigation"
import { useEffect, useRef } from "react"

interface ShowcaseModuleTrackingScriptProps {
    showcaseId: string
    trackingTitle: string
    productData: ProductData[]
}

export default function ShowcaseModuleTrackingScript({
    showcaseId,
    trackingTitle,
    productData
}: ShowcaseModuleTrackingScriptProps) {
    const hasTrackedRef = useRef(false)
    const isOutOfViewRef = useRef(false)
    const { site_country, site_language } = useSiteContext()
    const path = usePathname()
    const pageName = getPageNameFromPath(path)

    const productCacheKey = productData.map(product => product.productId).join(",")

    useEffect(() => {
        const showcaseElement = document.getElementById(showcaseId)

        if (!showcaseElement || productData.length === 0) return

        const observer = new IntersectionObserver(
            entries => {
                const entry = entries[0]

                if (!entry.isIntersecting) {
                    isOutOfViewRef.current = true
                    return
                }

                if (hasTrackedRef.current && !isOutOfViewRef.current) return

                hasTrackedRef.current = true
                isOutOfViewRef.current = false

                try {
                    const listId = `${pageName}|${trackingTitle}`
                    const trackingItems = productData.map(product =>
                        adaptProductForTracking(product, {
                            item_list_name: "showcase",
                            item_list_id: listId,
                            quantity: 1
                        })
                    )

                    import("@/lib/utils/tracking/gtmEvents").then(
                        ({ viewItemListEvent }) => {
                            viewItemListEvent(
                                trackingItems,
                                "showcase",
                                listId,
                                { site_country, site_language },
                                "showcase",
                                listId
                            )
                        }
                    )
                } catch (error) {
                    console.error("Error in showcase tracking:", error)
                }
            },
            { threshold: 0.1 }
        )

        observer.observe(showcaseElement)

        return () => {
            observer.disconnect()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        showcaseId,
        trackingTitle,
        pageName,
        productCacheKey,
        site_country,
        site_language
    ])

    return null
}
